import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { t } from 'ttag';
import UNIID from '../../sharedSvgs/uniid.svg';
import CustomizationOverlay from './CustomizationOverlay';

function CustomizationButton({ productID, isPDP, lineID }) {
  const [isDisabled, toggleDisabled] = useState(isPDP);
  const [hasError, toggleError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showCustomizationOverlay, toggleCustomizationOverlay] =
    useState(false);

  /**
   * We use the useSelector and useDispatch Redux Hooks to fetch and send information to and from Redux.
   */
  const sizeDetails = useSelector(
    (state) => state.app.selections.sizeDetails,
    shallowEqual
  );

  const dispatch = useDispatch();

  const hasVAS = useMemo(() => {
    if (
      window.__INITIAL_STATE__ &&
      window.__INITIAL_STATE__.value_added_service_applicable
    ) {
      return window.__INITIAL_STATE__.value_added_service_applicable;
    }
    return false;
  });

  const buttonClassNames = useMemo(
    () =>
      [
        'flex',
        'w-full',
        'items-center',
        'border',
        'border-solid',
        hasError ? 'border-cinnebar' : 'border-gray',
        'bg-white',
        'px-4',
        'py-4',
        'space-x-4',
        'text-sm',
        'font-semibold',
        'text-black',
        isDisabled ? 'opacity-50' : 'opacity-100',
        'hover:cursor-pointer',
        'disabled:hover:cursor-auto',
      ].join(' '),
    [isDisabled, hasError]
  );

  /**
   * We use the useEffect() React Hook to check if the Button should be disabled or enabled when sizeDetails updates.
   */
  useEffect(() => {
    toggleError(false); // Remove the Error Message if the sizeDetails have changed.

    if (!sizeDetails && isPDP) {
      setErrorMessage(t`Please choose size first`);
      toggleDisabled(true);
    } else if (sizeDetails && !sizeDetails.is_customizable && isPDP) {
      setErrorMessage(
        t`Unisport Partner products marked with * in the size list can't be customised`
      );
      toggleDisabled(true);
    } else if (sizeDetails && sizeDetails.is_customizable) {
      toggleDisabled(false);
      setErrorMessage('');
    }
  }, [sizeDetails, isPDP]);

  const handleClick = useCallback(() => {
    if (isDisabled) {
      toggleError(true);
    }
    if (!isDisabled) {
      // Display the Customization Overlay.
      toggleCustomizationOverlay(true);
      dispatch({ type: 'SELECTING_CUSTOMIZATION', payload: true });
    }
  }, [isDisabled]);

  return (
    <>
      <button
        type="button"
        onClick={() => handleClick()}
        className={buttonClassNames}
        data-testid="customizeProduct"
      >
        <img src={UNIID} alt="UNIID" />
        <span>{hasVAS ? t`Add protection and print` : t`Add print`}</span>
      </button>
      <span className="text-xs text-cinnebar">{hasError && errorMessage}</span>
      {showCustomizationOverlay && (
        <CustomizationOverlay
          toggleVisible={toggleCustomizationOverlay}
          productID={productID}
          showQuestion={false}
          isPDP={isPDP}
          lineID={lineID}
        />
      )}
    </>
  );
}

CustomizationButton.propTypes = {
  productID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  isPDP: PropTypes.bool,
  lineID: PropTypes.number,
};

CustomizationButton.defaultProps = {
  isPDP: false,
  lineID: undefined,
};

export default CustomizationButton;
